import React from "react"

import { AdminBonus2, AdminPlayerBonus2, BonusCurrencyRow, BonusViolation, BonusViolationCount } from "services/bonus/type"

import { BonusAnalytics } from "../components/analytics/BonusAnalytics"
import { ArchivedBonusListTable, BonusListTable } from "../components/bonus-config/BonusListTable"
import { FreeSpinsManager } from "../components/free-spins/FreeSpinsManager"
import { GameGroupManagement } from "../components/game-groups/GameGroupManagement"
import { AssignBonus } from "../components/player-bonuses/AssignBonus"
import { PlayerBonusManager } from "../components/player-bonuses/PlayerBonusManager"
import { PromoCodeManager } from "../components/promo-codes/PromoCodeManager"
import { ViolationsDashboard } from "../components/violations/ViolationsDashboard"

// ============================================
// TYPES
// ============================================

export type BonusTabType =
  | "bonuses"
  | "archive"
  | "assign"
  | "players"
  | "free-spins"
  | "promo-codes"
  | "game-groups"
  | "violations"
  | "analytics"

// ============================================
// CONSTANTS
// ============================================

export const BONUS_TABS: Array<{ id: BonusTabType; label: string }> = [
  { id: "bonuses", label: "bonus2.opt.tabs.bonuses" },
  // SOFT-644 — retired templates. Kept next to "Bonuses" because it is the same catalog,
  // just the archived partition of it.
  { id: "archive", label: "bonus2.opt.tabs.archive" },
  { id: "assign", label: "bonus2.opt.tabs.assign" },
  { id: "players", label: "bonus2.opt.tabs.players" },
  { id: "free-spins", label: "bonus2.opt.tabs.freeSpins" },
  { id: "promo-codes", label: "bonus2.opt.tabs.promoCodes" },
  { id: "game-groups", label: "bonus2.opt.tabs.gameGroups" },
  { id: "violations", label: "bonus2.opt.tabs.violations" },
  { id: "analytics", label: "bonus2.opt.tabs.analytics" }
]

export const TAB_CONTENT: Record<BonusTabType, React.FC<{ tenant: string }>> = {
  bonuses: BonusListTable,
  archive: ArchivedBonusListTable,
  players: PlayerBonusManager,
  assign: AssignBonus,
  "free-spins": FreeSpinsManager,
  "promo-codes": PromoCodeManager,
  "game-groups": GameGroupManagement,
  violations: ViolationsDashboard,
  analytics: BonusAnalytics
}

export const DEFAULT_BONUS_FORM_DATA: Partial<AdminBonus2> = {
  name: "",
  type: "DEPOSIT",
  mode: "NON_STICKY",
  calculation_mode: "PERCENTAGE",
  amount: 100,
  // Prefilled recommended defaults so a new bonus is valid and sensible out of the box.
  max_bonus_amount: 500, // cap the matched bonus at $500 by default
  min_deposit: 20, // typical qualifying deposit
  wagering_multiplier: 35,
  // Monolith-match defaults: requirement = multiplier × BONUS amount, and only bonus-funded bets
  // count (cash play is free). Requires the engine to fund bets from the bonus balance first
  // (see bonus-wallet.service funding) so bonus bets actually occur — otherwise wagering sticks.
  wagering_base: "BONUS_ONLY",
  wagering_contribution: "BONUS_BETS_ONLY",
  wagering_accounts: "BOTH_BALANCES",
  // Max Cashout defaults to a multiplier on the same base as wagering (a common, safe cap).
  max_cashout_multiplier: 10,
  max_cashout_base: "DEPOSIT_PLUS_BONUS",
  // Max bet: NO cap by default — the admin adds a limit only if they want one.
  activation_event: "DEPOSIT",
  expiration_days: 30,
  priority: 10,
  // Verification requirements OFF by default (= not required), matching the platform default.
  kyc_required: false,
  email_verified_required: false,
  phone_verified_required: false,
  stacking_mode: "NO_STACKING",
  // Sensible violation defaults (Req 7): log everything; block launch on restricted games.
  max_bet_actions: ["EXCLUDE_WAGERING", "ADD_LOG"],
  restricted_game_actions: ["PREVENT_LAUNCH", "ADD_LOG"],
  // Fix 14 — Eligible Payments: BOTH fiat and crypto selected by default (ticket requirement),
  // so a new bonus activates on any deposit type. (An empty list also means "both" on the
  // backend, but seeding both makes the checkboxes render CHECKED, not blank.)
  eligible_payments: ["FIAT", "CRYPTO"],
  active: true
}

// ============================================
// MONEY UNIT CONVERSION (dollars ⇄ cents)
// ============================================
// The backend stores these fields as BigInt MINOR UNITS (cents). The admin form works in
// DOLLARS for a nicer UX, so we convert: cents→dollars when loading an existing bonus, and
// dollars→cents when saving. `amount` is cents ONLY for a FIXED calculation_mode (for
// PERCENTAGE / AI it's a percent, not money — never converted).
// `max_cashout_amount` stays raw cents in the UI (its field is explicitly labelled "cents").
const DOLLAR_FIELDS = ["min_deposit", "max_bonus_amount", "max_bet_bonus", "max_bet_wagering"] as const

const toDollars = (cents?: number | null): number | undefined => (cents === null || cents === undefined ? undefined : cents / 100)
// `null` must survive the conversion: it is the payload's "clear this", and collapsing it to
// `undefined` here would turn a cleared min_deposit / max_bonus_amount / max_bet_* back into
// "leave alone".
const toCents = (dollars?: number | null): number | null | undefined =>
  dollars === null ? null : dollars === undefined ? undefined : Math.round(dollars * 100)

// Convert a loaded bonus (cents from backend) → dollars for the form.
export const bonusCentsToDollars = (b: Partial<AdminBonus2>): Partial<AdminBonus2> => {
  const out: Partial<AdminBonus2> = { ...b }
  for (const f of DOLLAR_FIELDS) out[f] = toDollars(b[f] as number | undefined) as never
  // FIXED bonus `amount` is a money value (cents) → dollars. PERCENTAGE/AI amount is a % — leave.
  if (b.calculation_mode === "FIXED") out.amount = toDollars(b.amount as number | undefined) as never
  return out
}

// Convert form state (dollars) → cents for the backend on save.
export const bonusDollarsToCents = (b: Partial<AdminBonus2>): Partial<AdminBonus2> => {
  const out: Partial<AdminBonus2> = { ...b }
  for (const f of DOLLAR_FIELDS) out[f] = toCents(b[f] as number | null | undefined) as never
  if (b.calculation_mode === "FIXED") out.amount = toCents(b.amount as number | null | undefined) as never
  return out
}

/**
 * An emptied optional input sends `null`, which the backend reads as "clear this". `undefined`
 * would mean "not in this payload" and leave the stored value in place.
 */
export const numOrClear = (raw: string): number | null => {
  if (raw.trim() === "") return null
  const n = parseFloat(raw)
  return Number.isNaN(n) ? null : n
}

export const intOrClear = (raw: string): number | null => {
  if (raw.trim() === "") return null
  const n = parseInt(raw, 10)
  return Number.isNaN(n) ? null : n
}

/**
 * What a type change does to the rest of the form: the new type's recommended config, then the
 * visibility sweep. Preview and apply BOTH call this, so the dialog can never describe something
 * different from what the click performs.
 */
/**
 * Fields a save must never remove:
 *  - `promo_codes` are attached from their own tab and are not this form's to delete.
 *  - `amount` / `free_spins_wagering` are non-nullable columns, so "removed" is not a state they
 *    have. A type that grants no cash simply never reads them.
 */
const NEVER_CLEARED: ReadonlySet<string> = new Set(["promo_codes", "amount", "free_spins_wagering"])

/**
 * The fields a config has no use for, as `{ field: null }` — the payload's "remove this".
 *
 * Applied when SAVING, never while editing: what the bonus ends up as decides what it keeps, so
 * changing a type and changing it back costs nothing.
 */
const cashCellCurrencies = (rows: unknown): string[] =>
  Array.isArray(rows)
    ? (rows as BonusCurrencyRow[])
        .filter((r) => r && (r.min_deposit !== undefined || r.max_bonus_amount !== undefined || r.amount !== undefined))
        .map((r) => (r.currency || "").toUpperCase())
        .filter(Boolean)
    : []

export const clearedByConfig = (d: Partial<AdminBonus2>): Record<string, null> => {
  const cleared: Record<string, null> = {}
  for (const key of Object.keys(FIELD_VISIBILITY)) {
    const rule = FIELD_VISIBILITY[key as keyof AdminBonus2]
    if (!rule || rule.hiddenValue || NEVER_CLEARED.has(key)) continue
    if (!rule.visible(d)) cleared[key] = null
  }
  return cleared
}

/** The per-currency rows as the saved config should carry them: no cells it cannot use. */
export const rowsForConfig = (d: Partial<AdminBonus2>): unknown =>
  isPureFreeSpinsType(d) ? stripCashCellsFromRows(d.currencies) : stripModeCellsFromRows(d.currencies, d.calculation_mode)

export const applyTypeChange = (d: Partial<AdminBonus2>, type: AdminBonus2["type"]): Partial<AdminBonus2> => {
  const recommended = TYPE_RECOMMENDED_CONFIG[type] ?? {}
  // A type carries a recommended calculation mode, so picking one can flip FIXED/PERCENTAGE
  // without the mode selector. `amount` means a percent in one and dollars in the other, so it
  // has to go the same way it does on a direct mode change — or 100% silently becomes $100.
  const modeFlipped = Boolean(recommended.calculation_mode) && recommended.calculation_mode !== d.calculation_mode
  return reconcileFieldVisibility({
    ...d,
    ...recommended,
    ...(modeFlipped ? { amount: undefined } : {}),
    type,
    wagering_accounts: "BOTH_BALANCES"
  })
}

export interface TypeChangeEffect {
  field: string
  from: unknown
  to: unknown
  /** Saving under the new type REMOVES this value from the bonus. */
  dropped: boolean
  /** Still used by the new type, but cleared so the operator restates it in the new unit. */
  reenter?: boolean
  /** A human description where a row count says nothing useful. */
  detail?: string
}

const hasValue = (v: unknown): boolean => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)

/**
 * The settings a type change would overwrite or stop using — only those that currently hold a
 * value, since a field going from empty to a recommendation costs the operator nothing.
 */
export const previewTypeChange = (d: Partial<AdminBonus2>, type: AdminBonus2["type"]): TypeChangeEffect[] => {
  const after = applyTypeChange(d, type)
  const cleared = clearedByConfig(after)
  const effects: TypeChangeEffect[] = []

  for (const key of Object.keys({ ...d, ...after })) {
    if (key === "type" || key === "currencies") continue
    const from = d[key as keyof AdminBonus2]
    if (!hasValue(from)) continue

    if (key in cleared) {
      effects.push({ field: key, from, to: null, dropped: true })
      continue
    }
    // Hidden, not removable, and not forced to anything: it just goes dormant, and saying so would
    // promise a change that never happens. A LOCKED field is hidden too but IS written, so it
    // stays reported.
    const rule = FIELD_VISIBILITY[key as keyof AdminBonus2]
    if (rule && !rule.visible(after) && !rule.hiddenValue) continue

    const to = after[key as keyof AdminBonus2]
    if (from === to) continue
    // Still in use, but cleared for re-entry — `amount` after a FIXED/PERCENTAGE flip.
    effects.push({ field: key, from, to, dropped: false, reenter: !hasValue(to) })
  }

  // A row count either side would say "1 row → 1 row" while its amounts are being deleted, so the
  // currencies losing a cell are named instead.
  const keeping = cashCellCurrencies(rowsForConfig(after))
  const losing = cashCellCurrencies(d.currencies).filter((c) => !keeping.includes(c))
  if (losing.length > 0) {
    effects.push({ field: "currencies", from: d.currencies, to: null, dropped: true, detail: losing.join(", ") })
  }
  return effects
}

export interface ViolationGroup {
  key: string
  userId: string
  username?: string
  violationType: string
  count: number
  rows: BonusViolation[]
}

export const groupViolations = (rows: BonusViolation[], counts: BonusViolationCount[]): ViolationGroup[] => {
  const keyOf = (userId: string, type: string) => `${userId}::${type}`
  const groups = new Map<string, ViolationGroup>()

  for (const c of counts) {
    groups.set(keyOf(c.user_id, c.violation_type), {
      key: keyOf(c.user_id, c.violation_type),
      userId: c.user_id,
      username: c.username,
      violationType: c.violation_type,
      count: c.count,
      rows: []
    })
  }

  for (const row of rows) {
    const userId = row.player_bonus?.user_id ?? ""
    const key = keyOf(userId, row.violation_type)
    const group = groups.get(key) ?? {
      key,
      userId,
      username: row.username,
      violationType: row.violation_type,
      count: row.violation_count ?? 0,
      rows: []
    }
    group.username = group.username ?? row.username
    group.rows.push(row)
    groups.set(key, group)
  }

  return Array.from(groups.values()).sort((a, b) => b.count - a.count)
}

export const violationCountsByType = (counts: BonusViolationCount[]): Record<string, number> =>
  counts.reduce<Record<string, number>>((acc, c) => {
    acc[c.violation_type] = (acc[c.violation_type] ?? 0) + c.count
    return acc
  }, {})

export const spinsGamesOutsideAllowList = (d: Partial<AdminBonus2>): string[] => {
  const allowed = d.allowed_game_ids ?? []
  if (allowed.length === 0) return []
  return (d.free_spins_game_ids ?? []).filter((id) => !allowed.includes(id))
}

export const isNoDepositType = (t?: AdminBonus2["type"]) => t === "NO_DEPOSIT" || t === "FREE_SPINS" || t === "CASHBACK" || t === "REFERRAL"

// ============================================
// FIELD VISIBILITY / LOCKING — single source of truth
// ============================================
// A field that's hidden/locked in the UI for some bonus config must ALSO be reset in
// `formData` when it becomes irrelevant — otherwise a stale value (set while the field WAS
// visible, e.g. under a different type) silently rides along in the payload, invisible to the
// admin, until they happen to revisit whatever state made it editable again and manually clear
// it. `useBonusForm`'s single reconciliation effect walks this table on every type/calculation-
// mode change and corrects every field listed here in one pass — so a field's hide condition
// and its "what happens to the value when hidden" live in exactly ONE place. To add a new
// locked/irrelevant field, add one entry here — no new `useEffect` or JSX condition needed
// (JSX visibility conditions should call the SAME `is*`/`show*` flags this table is built from,
// so the two can't drift apart).
//
// - `hiddenValue` present  → the field is LOCKED: forced to this value whenever `visible` is
//   false (it's not just irrelevant, it has one correct value the backend expects — e.g.
//   wagering_base must still be "BONUS_ONLY", not undefined).
// - `hiddenValue` absent   → the field is simply IRRELEVANT when hidden: cleared to `undefined`.
// DEPOSIT_AND_SPINS ("Freespins+Deposit") DOES have a cash component — the deposit match — so
// it follows the standard deposit rules (Mode/Calculation Mode, Cap Based On, Wagering Base /
// Which Bets Count all stay editable, matching every other deposit-triggered type). Only the
// genuinely no-cash types are locked here.
export const hasNoCashComponent = (d: Partial<AdminBonus2>) => d.type === "NO_DEPOSIT" || d.type === "FREE_SPINS"
export const isFreeSpinsType = (d: Partial<AdminBonus2>) => d.type === "FREE_SPINS" || d.type === "DEPOSIT_AND_SPINS"
export const isPureNoDepositType = (d: Partial<AdminBonus2>) => d.type === "NO_DEPOSIT"
export const isPureFreeSpinsType = (d: Partial<AdminBonus2>) => d.type === "FREE_SPINS"

export interface FieldVisibilityRule {
  visible: (d: Partial<AdminBonus2>) => boolean
  hiddenValue?: (d: Partial<AdminBonus2>) => unknown
  // Applied while the field IS visible, for a COMPOSITE value whose parts are not all relevant
  // at once (the per-currency rows: a pure Free-Spins bonus keeps the rows for their spin stake
  // but has no cash amounts to put in them). MUST return the same reference when nothing needs
  // changing — `reconcileFieldVisibility` runs on every render pass and a fresh object each time
  // would loop.
  sanitize?: (value: unknown, d: Partial<AdminBonus2>) => unknown
}

// SOFT-736 — a pure Free-Spins bonus has no cash amounts, so its per-currency rows carry ONLY the
// spin stake. Drops the cash cells (and any row left with nothing at all), preserving the original
// array when there is nothing to drop.
/**
 * Drop the per-currency cash cell the new mode does not use — `amount` under PERCENTAGE,
 * `max_bonus_amount` under FIXED. A leftover row `amount` is read as the percentage at activation.
 */
export const stripModeCellsFromRows = (value: unknown, mode: AdminBonus2["calculation_mode"] | undefined): unknown => {
  // No mode yet (first render, before defaults) ⇒ strip nothing. Failing open cannot lose a cell.
  if (!Array.isArray(value) || !mode) return value
  const drop: keyof BonusCurrencyRow = mode === "PERCENTAGE" ? "amount" : "max_bonus_amount"
  let changed = false
  const next = (value as BonusCurrencyRow[]).map((row) => {
    if (!row || row[drop] === undefined) return row
    changed = true
    const copy = { ...row }
    delete copy[drop]
    return copy
  })
  return changed ? next : value
}

const stripCashCellsFromRows = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value
  const rows = value as BonusCurrencyRow[]
  let changed = false
  const next: BonusCurrencyRow[] = []
  for (const row of rows) {
    if (!row) continue
    if (row.min_deposit === undefined && row.max_bonus_amount === undefined && row.amount === undefined) {
      next.push(row)
      continue
    }
    changed = true
    next.push({ currency: row.currency, free_spins_bet_value: row.free_spins_bet_value })
  }
  return changed ? next : value
}

export const FIELD_VISIBILITY: Partial<Record<keyof AdminBonus2, FieldVisibilityRule>> = {
  // Tickets 3 & 8 — Mode / Calculation Mode locked for No-Deposit & Free-Spins bonuses.
  mode: { visible: (d) => !hasNoCashComponent(d), hiddenValue: () => "STICKY" },
  calculation_mode: { visible: (d) => !hasNoCashComponent(d), hiddenValue: () => "FIXED" },
  // Tickets 1 & 5 — wagering base/contribution locked for the same set of types.
  wagering_base: { visible: (d) => !hasNoCashComponent(d), hiddenValue: () => "BONUS_ONLY" },
  wagering_contribution: { visible: (d) => !hasNoCashComponent(d), hiddenValue: () => "BONUS_BETS_ONLY" },
  // Tickets 2 & 7 — cashout cap base locked to Bonus Amount for the same set of types.
  max_cashout_base: { visible: (d) => !hasNoCashComponent(d), hiddenValue: () => "BONUS_ONLY" },
  // Ticket 2 — no deposit is involved for a pure No-Deposit bonus, so min_deposit is irrelevant
  // there. Free Spins KEEPS min_deposit — a deposit threshold can still gate/trigger a spins
  // grant even though Free Spins has no cash MATCH amount (see the `amount` rule below). Cleared,
  // not locked — there's no single "correct" min deposit for an irrelevant field.
  min_deposit: { visible: (d) => !isPureNoDepositType(d) },
  // Bugfix — a pure Free-Spins bonus has NO cash-match component at all (the reward is the
  // spins themselves, configured below); `amount`/`max_bonus_amount` and the entire per-currency
  // "Amounts by currency" table are irrelevant. Matches `showPercentageAmount` in useBonusForm.ts
  // exactly (`!isPureFreeSpinsType`) — that's what gates the equivalent JSX in BonusFormSections.
  amount: { visible: (d) => !isPureFreeSpinsType(d) },
  max_bonus_amount: { visible: (d) => d.calculation_mode === "PERCENTAGE" },
  // SOFT-736 — the per-currency TABLE survives for a pure Free-Spins bonus even though its cash
  // columns do not: a row is the only place a per-currency SPIN STAKE can be set, and the stake
  // has to be a real tier of the game or the player silently never gets the spins. Erasing the
  // rows here (the previous `!isPureFreeSpinsType` rule, applied again inside handleSubmit) meant
  // a form that accepted a stake and dropped it on the way to the server. What is irrelevant for
  // this type is the cash CELLS, so those are stripped per row instead.
  currencies: { visible: () => true, hiddenValue: () => [] },
  // AI_DYNAMIC-only range fields — irrelevant (and previously left stale) for any other mode.
  amount_min: { visible: (d) => d.calculation_mode === "AI_DYNAMIC" },
  amount_max: { visible: (d) => d.calculation_mode === "AI_DYNAMIC" },
  // No hiddenValue, deliberately: a code is a doorway into a bonus, not its trigger, so the
  // Promo Codes tab attaches codes to bonuses of any activation_event. Clearing the set here
  // would destroy codes this form is not showing.
  promo_codes: { visible: (d) => d.activation_event === "PROMO_CODE" },
  // Free Spins Settings — irrelevant (and previously left stale) outside Free-Spins types.
  free_spins_count: { visible: isFreeSpinsType },
  free_spins_bet_value: { visible: isFreeSpinsType },
  free_spins_game_ids: { visible: isFreeSpinsType },
  free_spins_wagering: { visible: isFreeSpinsType }
}

// Applies every FIELD_VISIBILITY rule to `d`, returning a NEW object only when at least one
// field actually needed correcting (so callers can bail out on an unchanged reference and avoid
// a render loop). Pure function — no state, trivially unit-testable.
export const reconcileFieldVisibility = (d: Partial<AdminBonus2>): Partial<AdminBonus2> => {
  let changed = false
  const next: Partial<AdminBonus2> = { ...d }
  const correct = (key: keyof AdminBonus2, value: unknown) => {
    if (next[key] === value) return
    next[key] = value as never
    changed = true
  }
  for (const key of Object.keys(FIELD_VISIBILITY) as Array<keyof AdminBonus2>) {
    const rule = FIELD_VISIBILITY[key]
    if (!rule) continue
    // Hidden → forced to its locked value (or cleared). Visible → only a composite value can
    // still be partly irrelevant, which is what `sanitize` trims.
    // A LOCKED field is forced, because the UI must show the value the backend will get. A merely
    // IRRELEVANT one is left alone: this runs on every type change, and emptying form state here
    // is what made a toggle destructive. What a save drops is decided by `clearedByConfig`.
    if (!rule.visible(d)) {
      if (rule.hiddenValue) correct(key, rule.hiddenValue(d))
    } else if (rule.sanitize) correct(key, rule.sanitize(next[key], d))
  }
  return changed ? next : d
}

// Recommended wagering/cashout BASE. MONOLITH-MATCH: the requirement is always sized off the
// BONUS amount (the old engine did `wager_requirements × bonus_amount`, never deposit). So this
// returns BONUS_ONLY for every type/mode. (Kept as a function so the type cascade, mode-change
// handler, and presets all read one source and can't drift.)
export const recommendedBase = (_type?: AdminBonus2["type"], _mode?: AdminBonus2["mode"]): "BONUS_ONLY" | "DEPOSIT_PLUS_BONUS" =>
  "BONUS_ONLY"

// max_active_bonuses that a stacking_mode implies (docs cross-field rule): NO_STACKING → 1,
// any stacking mode → allow more (default 3).
export const stackingActiveBonuses = (mode?: AdminBonus2["stacking_mode"]): number => (mode === "NO_STACKING" ? 1 : 3)

// Per-type recommended config cascade. When the admin picks a bonus Type, we adjust the
// downstream fields to values that make sense for that type (only overriding config knobs,
// never the admin's name). Each entry is merged over the current formData.
export const TYPE_RECOMMENDED_CONFIG: Record<AdminBonus2["type"], Partial<AdminBonus2>> = {
  // Standard deposit match — the canonical case.
  DEPOSIT: {
    calculation_mode: "PERCENTAGE",
    activation_event: "DEPOSIT",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "NON_STICKY"
  },
  // Reload — like deposit but usually a lower match, triggered on reload deposits.
  RELOAD: {
    calculation_mode: "PERCENTAGE",
    activation_event: "RELOAD_DEPOSIT",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "NON_STICKY"
  },
  // No-deposit — a fixed granted amount, no deposit to base on → wager the bonus only, sticky.
  NO_DEPOSIT: {
    calculation_mode: "FIXED",
    activation_event: "NO_TRIGGER",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "STICKY",
    max_cashout_base: "BONUS_ONLY"
  },
  // Cashback — fixed/percentage rebate, admin-granted, usually low or no wagering.
  CASHBACK: {
    calculation_mode: "FIXED",
    activation_event: "NO_TRIGGER",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    wagering_multiplier: 1,
    mode: "NON_STICKY"
  },
  // Free spins — reward is spins, no cash match; winnings wager on the bonus balance, sticky.
  FREE_SPINS: {
    calculation_mode: "FIXED",
    activation_event: "NO_TRIGGER",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "STICKY",
    max_cashout_base: "BONUS_ONLY",
    // Seeded here, not in DEFAULT_BONUS_FORM_DATA: the field is hidden for the default DEPOSIT
    // type, and the visibility reconcile clears every hidden field on mount. 1x is what
    // bonus-service and the column already default to; 0 means no wagering at all.
    free_spins_wagering: 1
  },
  // Deposit + spins — a deposit match PLUS free spins.
  DEPOSIT_AND_SPINS: {
    calculation_mode: "PERCENTAGE",
    activation_event: "DEPOSIT",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "NON_STICKY",
    free_spins_wagering: 1
  },
  // Referral — fixed reward granted on a referral event.
  REFERRAL: {
    calculation_mode: "FIXED",
    activation_event: "NO_TRIGGER",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    mode: "STICKY"
  },
  // VIP — fixed loyalty reward, admin-granted, often lenient wagering.
  VIP: {
    calculation_mode: "FIXED",
    activation_event: "NO_TRIGGER",
    wagering_base: "BONUS_ONLY",
    wagering_contribution: "BONUS_BETS_ONLY",
    wagering_multiplier: 10,
    mode: "NON_STICKY"
  }
}

// ============================================
// FIELD RANGES (min/max) + TOOLTIPS
// ============================================

// Recommended numeric ranges. Validation is a non-blocking inline WARNING (backend is the
// final validator). `soft` = a recommendation band, not a hard limit.
export interface FieldRange {
  min?: number
  max?: number
  soft?: boolean
  unit?: string
}

export const FIELD_RANGES: Record<string, FieldRange> = {
  amount: { min: 0, max: 1000, soft: true, unit: "% or $" },
  max_bonus_amount: { min: 0, max: 100000, soft: true, unit: "$" },
  min_deposit: { min: 0, max: 100000, soft: true, unit: "$" },
  wagering_multiplier: { min: 0, max: 200, soft: true, unit: "x" },
  max_cashout_multiplier: { min: 0, max: 100, soft: true, unit: "x" },
  max_cashout_amount: { min: 0, max: 100000000, soft: true, unit: "cents" },
  max_bet_bonus: { min: 0, max: 1000, soft: true, unit: "$" },
  max_bet_wagering: { min: 0, max: 1000, soft: true, unit: "$" },
  expiration_days: { min: 1, max: 365, unit: "days" },
  priority: { min: 0, max: 1000, soft: true },
  offered_on_deposit_number: { min: 1, max: 5, unit: "deposit #" },
  free_spins_count: { min: 0, max: 1000, soft: true },
  free_spins_bet_value: { min: 0, max: 100000, soft: true, unit: "cents" },
  free_spins_wagering: { min: 0, max: 100, soft: true, unit: "x" },
  max_activations_per_player: { min: 1, max: 1000, soft: true },
  cooldown_days: { min: 0, max: 365, soft: true, unit: "days" }
}

// Return a non-blocking warning string when a value is outside its recommended range, else null.
export const rangeWarning = (field: string, value: number | undefined | null): string | null => {
  const r = FIELD_RANGES[field]
  if (!r || value === null || value === undefined || Number.isNaN(value)) return null
  if (r.min !== undefined && value < r.min) return `Recommended ≥ ${r.min}${r.unit ? " " + r.unit : ""}`
  if (r.max !== undefined && value > r.max) return `Recommended ≤ ${r.max}${r.unit ? " " + r.unit : ""}`
  return null
}

// Detailed tooltip text per field (shown on hover of the ? icon). Plain-language, explains
// what the field does and how it interacts with the rest of the bonus.
export const FIELD_TOOLTIPS: Record<string, string> = {
  name: "bonus2.tooltip.name",
  type: "bonus2.tooltip.type",
  mode: "bonus2.tooltip.mode",
  calculation_mode: "bonus2.tooltip.calculation_mode",
  amount: "bonus2.tooltip.amount",
  max_bonus_amount: "bonus2.tooltip.max_bonus_amount",
  min_deposit: "bonus2.tooltip.min_deposit",
  wagering_multiplier: "bonus2.tooltip.wagering_multiplier",
  wagering_base: "bonus2.tooltip.wagering_base",
  wagering_contribution: "bonus2.tooltip.wagering_contribution",
  max_bet_bonus: "bonus2.tooltip.max_bet_bonus",
  max_bet_wagering: "bonus2.tooltip.max_bet_wagering",
  max_cashout_multiplier: "bonus2.tooltip.max_cashout_multiplier",
  max_cashout_base: "bonus2.tooltip.max_cashout_base",
  max_cashout_amount: "bonus2.tooltip.max_cashout_amount",
  activation_event: "bonus2.tooltip.activation_event",
  offered_on_deposit_number: "bonus2.tooltip.offered_on_deposit_number",
  expiration_days: "bonus2.tooltip.expiration_days",
  priority: "bonus2.tooltip.priority",
  stacking_mode: "bonus2.tooltip.stacking_mode",
  starts_at_iso: "bonus2.tooltip.starts_at_iso",
  ends_at_iso: "bonus2.tooltip.ends_at_iso",
  max_activations_per_player: "bonus2.tooltip.max_activations_per_player",
  cooldown_days: "bonus2.tooltip.cooldown_days",
  free_spins_count: "bonus2.tooltip.free_spins_count",
  free_spins_bet_value: "bonus2.tooltip.free_spins_bet_value",
  free_spins_wagering: "bonus2.tooltip.free_spins_wagering",
  promo_codes: "bonus2.tooltip.promo_code",
  description: "bonus2.tooltip.description",
  banner_id: "bonus2.tooltip.banner_id",
  allowed_products: "bonus2.tooltip.allowed_products",
  max_bet_actions: "bonus2.tooltip.max_bet_actions",
  restricted_game_actions: "bonus2.tooltip.restricted_game_actions",
  // Per-action tooltips
  action_CANCEL_BONUS: "bonus2.tooltip.action_CANCEL_BONUS",
  action_EXCLUDE_WAGERING: "bonus2.tooltip.action_EXCLUDE_WAGERING",
  action_ADD_LOG: "bonus2.tooltip.action_ADD_LOG",
  action_PREVENT_LAUNCH: "bonus2.tooltip.action_PREVENT_LAUNCH"
}

// ============================================
// PER-SECTION PRESETS (context-aware)
// ============================================

// A preset is a named bundle of field values applied to ONE section. Presets are functions
// of the CURRENT formData so they adapt to earlier choices (e.g. wagering presets differ for
// a no-deposit bonus; cashout base follows the wagering base). `values` is merged over
// formData when the admin clicks Apply.
export interface SectionPreset {
  id: string
  title: string
  description: string
  summary: string[] // short human-readable list of what it sets (shown on the card)
  values: { [K in keyof AdminBonus2]?: AdminBonus2[K] | null }
}

/** The subset of i18next's `t` this module needs: a key plus optional interpolation values. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

// Returns presets for the given section, tailored to the current formData.
// `t` is passed in rather than applied by the card: a few presets interpolate the current
// cashout base into their text, and the render site has no way to supply those params.
export const getSectionPresets = (section: string, data: Partial<AdminBonus2>, t: TranslateFn): SectionPreset[] => {
  // Max Cashout presets — shown in the Amount section (that's where the cashout block lives).
  // Multiplier presets follow the wagering base; the fixed-amount preset uses cents. `10×` is
  // the value used in the docs' worked example. These set only cashout fields, so they compose
  // cleanly with whichever amount preset the admin picked.
  // Cashout base mirrors the wagering base (single source of truth).
  const cashoutBase = recommendedBase(data.type, data.mode)
  const cashoutBaseLabel = (cashoutBase === "BONUS_ONLY" ? t("bonus2.base.bonus") : t("bonus2.base.depositPlusBonus")) as string
  const cashoutPresets: SectionPreset[] = [
    {
      id: "cash-mult-3",
      title: t("bonus2.preset.cash_mult_3.title"),
      description: t("bonus2.preset.cash_mult_3.desc", { base: cashoutBaseLabel.toLowerCase() }),
      summary: [t("bonus2.preset.cash_mult_3.s0"), t("bonus2.preset.baseIs", { base: cashoutBaseLabel })],
      values: { max_cashout_multiplier: 3, max_cashout_base: cashoutBase, max_cashout_amount: null }
    },
    {
      id: "cash-mult-10",
      title: t("bonus2.preset.cash_mult_10.title"),
      description: t("bonus2.preset.cash_mult_10.desc", { base: cashoutBaseLabel.toLowerCase() }),
      summary: [t("bonus2.preset.cash_mult_10.s0"), t("bonus2.preset.baseIs", { base: cashoutBaseLabel })],
      values: { max_cashout_multiplier: 10, max_cashout_base: cashoutBase, max_cashout_amount: null }
    },
    {
      id: "cash-fixed-1000",
      title: t("bonus2.preset.cash_fixed_1000.title"),
      description: t("bonus2.preset.cash_fixed_1000.desc"),
      summary: [t("bonus2.preset.cash_fixed_1000.s0")],
      values: { max_cashout_amount: 100000, max_cashout_multiplier: null, max_cashout_base: null }
    },
    {
      id: "cash-none",
      title: t("bonus2.preset.cash_none.title"),
      description: t("bonus2.preset.cash_none.desc"),
      summary: [t("bonus2.preset.cash_none.s0")],
      values: { max_cashout_multiplier: null, max_cashout_base: null, max_cashout_amount: null }
    }
  ]

  switch (section) {
    case "amount": {
      // Presets never change the calculation mode or type (applyPreset enforces this). So the
      // Amount presets must MATCH the current calculation_mode — otherwise the card would
      // promise values the apply step (correctly) refuses to set. Offer percentage-match
      // presets in PERCENTAGE mode, flat-grant presets in FIXED mode. Cashout presets are
      // appended (the Max Cashout block lives in this section).
      let amountPresets: SectionPreset[]
      if (data.calculation_mode === "FIXED") {
        // Flat-grant tiers (no-deposit / cashback / VIP). Amounts follow the monolith's
        // WAGER_GOALS scale ($10/$50) — real reward tiers used in production.
        amountPresets = [
          {
            id: "amt-fixed-5",
            title: t("bonus2.preset.amt_fixed_5.title"),
            description: t("bonus2.preset.amt_fixed_5.desc"),
            summary: [t("bonus2.preset.amt_fixed_5.s0")],
            values: { amount: 5 }
          },
          {
            id: "amt-fixed-10",
            title: t("bonus2.preset.amt_fixed_10.title"),
            description: t("bonus2.preset.amt_fixed_10.desc"),
            summary: [t("bonus2.preset.amt_fixed_10.s0")],
            values: { amount: 10 }
          },
          {
            id: "amt-fixed-25",
            title: t("bonus2.preset.amt_fixed_25.title"),
            description: t("bonus2.preset.amt_fixed_25.desc"),
            summary: [t("bonus2.preset.amt_fixed_25.s0")],
            values: { amount: 25 }
          },
          {
            id: "amt-fixed-50",
            title: t("bonus2.preset.amt_fixed_50.title"),
            description: t("bonus2.preset.amt_fixed_50.desc"),
            summary: [t("bonus2.preset.amt_fixed_50.s0")],
            values: { amount: 50 }
          }
        ]
      } else if (data.calculation_mode === "AI_DYNAMIC") {
        amountPresets = [
          {
            id: "amt-ai-5-25",
            title: t("bonus2.preset.amt_ai_5_25.title"),
            description: t("bonus2.preset.amt_ai_5_25.desc"),
            summary: [t("bonus2.preset.amt_ai_5_25.s0"), t("bonus2.preset.amt_ai_5_25.s1")],
            values: { amount_min: 5, amount_max: 25 }
          },
          {
            id: "amt-ai-10-50",
            title: t("bonus2.preset.amt_ai_10_50.title"),
            description: t("bonus2.preset.amt_ai_10_50.desc"),
            summary: [t("bonus2.preset.amt_ai_10_50.s0"), t("bonus2.preset.amt_ai_10_50.s1")],
            values: { amount_min: 10, amount_max: 50 }
          },
          {
            id: "amt-ai-25-200",
            title: t("bonus2.preset.amt_ai_25_200.title"),
            description: t("bonus2.preset.amt_ai_25_200.desc"),
            summary: [t("bonus2.preset.amt_ai_25_200.s0"), t("bonus2.preset.amt_ai_25_200.s1")],
            values: { amount_min: 25, amount_max: 200 }
          }
        ]
      } else {
        // PERCENTAGE (default): deposit-match presets. 100%/$500 min-deposit $20 is the docs'
        // canonical "Welcome 100%" example.
        amountPresets = [
          {
            id: "amt-100-500",
            title: t("bonus2.preset.amt_100_500.title"),
            description: t("bonus2.preset.amt_100_500.desc") as string,
            summary: [t("bonus2.preset.amt_100_500.s0"), t("bonus2.preset.amt_100_500.s1"), t("bonus2.preset.amt_100_500.s2")],
            values: { amount: 100, max_bonus_amount: 500, min_deposit: 20 }
          },
          {
            id: "amt-200-100",
            title: t("bonus2.preset.amt_200_100.title"),
            description: t("bonus2.preset.amt_200_100.desc"),
            summary: [t("bonus2.preset.amt_200_100.s0"), t("bonus2.preset.amt_200_100.s1"), t("bonus2.preset.amt_200_100.s2")],
            values: { amount: 200, max_bonus_amount: 100, min_deposit: 10 }
          },
          {
            id: "amt-50-200",
            title: t("bonus2.preset.amt_50_200.title"),
            description: t("bonus2.preset.amt_50_200.desc"),
            summary: [t("bonus2.preset.amt_50_200.s0"), t("bonus2.preset.amt_50_200.s1"), t("bonus2.preset.amt_50_200.s2")],
            values: { amount: 50, max_bonus_amount: 200, min_deposit: 20 }
          },
          {
            id: "amt-100-1000",
            title: t("bonus2.preset.amt_100_1000.title"),
            description: t("bonus2.preset.amt_100_1000.desc"),
            summary: [t("bonus2.preset.amt_100_1000.s0"), t("bonus2.preset.amt_100_1000.s1"), t("bonus2.preset.amt_100_1000.s2")],
            values: { amount: 100, max_bonus_amount: 1000, min_deposit: 50 }
          }
        ]
      }
      return [...amountPresets, ...cashoutPresets]
    }

    case "wagering": {
      // Mode/type-aware base — single source of truth (recommendedBase).
      const base = recommendedBase(data.type, data.mode)
      const baseLabel = t("bonus2.preset.baseIs", {
        base: (base === "BONUS_ONLY" ? t("bonus2.base.bonus") : t("bonus2.base.depositPlusBonus")) as string
      }) as string
      // Trimmed to the 4 most useful tiers (Standard / Friendly / Generous / None). All use
      // ALL_BETS; base + contribution follow mode/type. Presets set only the wagering knobs —
      // max bet is left to the admin (no cap by default).
      return [
        {
          id: "wag-standard",
          title: t("bonus2.preset.wag_standard.title"),
          description: t("bonus2.preset.wag_standard.desc"),
          summary: [t("bonus2.preset.wag_standard.s0"), baseLabel, t("bonus2.preset.wag_standard.s1")],
          values: { wagering_multiplier: 35, wagering_base: base, wagering_contribution: "ALL_BETS" }
        },
        {
          id: "wag-30",
          title: t("bonus2.preset.wag_30.title"),
          description: t("bonus2.preset.wag_30.desc"),
          summary: [t("bonus2.preset.wag_30.s0"), baseLabel, t("bonus2.preset.wag_30.s1")],
          values: { wagering_multiplier: 30, wagering_base: base, wagering_contribution: "ALL_BETS" }
        },
        {
          id: "wag-20",
          title: t("bonus2.preset.wag_20.title"),
          description: t("bonus2.preset.wag_20.desc"),
          summary: [t("bonus2.preset.wag_20.s0"), baseLabel, t("bonus2.preset.wag_20.s1")],
          values: { wagering_multiplier: 20, wagering_base: base, wagering_contribution: "ALL_BETS" }
        },
        {
          id: "wag-none",
          title: t("bonus2.preset.wag_none.title"),
          description: t("bonus2.preset.wag_none.desc"),
          summary: [t("bonus2.preset.wag_none.s0"), t("bonus2.preset.wag_none.s1"), t("bonus2.preset.wag_none.s2")],
          // G3: 0× wagering ⇒ clear the cashout cap (a cap with nothing to wager is contradictory).
          values: {
            wagering_multiplier: 0,
            wagering_base: base,
            wagering_contribution: "ALL_BETS",
            max_cashout_multiplier: null,
            max_cashout_amount: null,
            max_cashout_base: null
          }
        }
      ]
    }

    case "violations":
      return [
        {
          id: "vio-lenient",
          title: t("bonus2.preset.vio_lenient.title"),
          description: t("bonus2.preset.vio_lenient.desc"),
          summary: [t("bonus2.preset.vio_lenient.s0"), t("bonus2.preset.vio_lenient.s1")],
          values: { max_bet_actions: ["ADD_LOG"], restricted_game_actions: ["ADD_LOG"] }
        },
        {
          id: "vio-standard",
          title: t("bonus2.preset.vio_standard.title"),
          description: t("bonus2.preset.vio_standard.desc"),
          summary: [t("bonus2.preset.vio_standard.s0"), t("bonus2.preset.vio_standard.s1")],
          values: { max_bet_actions: ["EXCLUDE_WAGERING", "ADD_LOG"], restricted_game_actions: ["PREVENT_LAUNCH", "ADD_LOG"] }
        },
        {
          id: "vio-cancel-maxbet",
          title: t("bonus2.preset.vio_cancel_maxbet.title"),
          description: t("bonus2.preset.vio_cancel_maxbet.desc"),
          summary: [t("bonus2.preset.vio_cancel_maxbet.s0"), t("bonus2.preset.vio_cancel_maxbet.s1")],
          values: { max_bet_actions: ["CANCEL_BONUS", "ADD_LOG"], restricted_game_actions: ["PREVENT_LAUNCH", "ADD_LOG"] }
        },
        {
          id: "vio-strict",
          title: t("bonus2.preset.vio_strict.title"),
          description: t("bonus2.preset.vio_strict.desc"),
          summary: [t("bonus2.preset.vio_strict.s0"), t("bonus2.preset.vio_strict.s1")],
          values: { max_bet_actions: ["CANCEL_BONUS", "ADD_LOG"], restricted_game_actions: ["CANCEL_BONUS", "PREVENT_LAUNCH", "ADD_LOG"] }
        }
      ]

    case "activation":
      // Type-aware: a no-deposit / fixed-grant bonus is NOT deposit-triggered, so only the
      // admin-granted preset applies — a deposit/welcome preset would set an invalid trigger.
      if (isNoDepositType(data.type)) {
        return [
          {
            id: "act-admin",
            title: t("bonus2.preset.act_admin.title"),
            description: t("bonus2.preset.act_admin.desc"),
            summary: [t("bonus2.preset.act_admin.s0"), t("bonus2.preset.act_admin.s1"), t("bonus2.preset.act_admin.s2")],
            values: {
              activation_event: "NO_TRIGGER",
              expiration_days: 7,
              offered_on_deposit_number: null,
              stacking_mode: "NO_STACKING"
            }
          },
          {
            id: "act-admin-30",
            title: t("bonus2.preset.act_admin_30.title"),
            description: t("bonus2.preset.act_admin_30.desc"),
            summary: [t("bonus2.preset.act_admin_30.s0"), t("bonus2.preset.act_admin_30.s1"), t("bonus2.preset.act_admin_30.s2")],
            values: {
              activation_event: "NO_TRIGGER",
              expiration_days: 30,
              offered_on_deposit_number: null,
              stacking_mode: "NO_STACKING"
            }
          }
        ]
      }
      return [
        {
          id: "act-deposit",
          title: t("bonus2.preset.act_deposit.title"),
          description: t("bonus2.preset.act_deposit.desc"),
          summary: [t("bonus2.preset.act_deposit.s0"), t("bonus2.preset.act_deposit.s1"), t("bonus2.preset.act_deposit.s2")],
          values: { activation_event: "DEPOSIT", expiration_days: 30, stacking_mode: "NO_STACKING" }
        },
        {
          id: "act-welcome",
          title: t("bonus2.preset.act_welcome.title"),
          description: t("bonus2.preset.act_welcome.desc"),
          summary: [t("bonus2.preset.act_welcome.s0"), t("bonus2.preset.act_welcome.s1"), t("bonus2.preset.act_welcome.s2")],
          values: { activation_event: "FIRST_DEPOSIT", offered_on_deposit_number: 1, expiration_days: 14, stacking_mode: "NO_STACKING" }
        },
        {
          id: "act-reload",
          title: t("bonus2.preset.act_reload.title"),
          description: t("bonus2.preset.act_reload.desc"),
          summary: [t("bonus2.preset.act_reload.s0"), t("bonus2.preset.act_reload.s1"), t("bonus2.preset.act_reload.s2")],
          values: { activation_event: "RELOAD_DEPOSIT", expiration_days: 7, stacking_mode: "NO_STACKING" }
        },
        {
          id: "act-promo",
          title: t("bonus2.preset.act_promo.title"),
          description: t("bonus2.preset.act_promo.desc"),
          summary: [t("bonus2.preset.act_promo.s0"), t("bonus2.preset.act_promo.s1"), t("bonus2.preset.act_promo.s2")],
          values: { activation_event: "PROMO_CODE", expiration_days: 30, stacking_mode: "NO_STACKING" }
        },
        {
          id: "act-admin",
          title: t("bonus2.preset.act_admin.title"),
          description: t("bonus2.preset.act_admin.desc"),
          summary: [t("bonus2.preset.act_admin.s0"), t("bonus2.preset.act_admin.s1")],
          values: { activation_event: "NO_TRIGGER", expiration_days: 7, offered_on_deposit_number: null }
        }
      ]

    case "advanced":
      return [
        {
          id: "adv-single",
          title: t("bonus2.preset.adv_single.title"),
          description: t("bonus2.preset.adv_single.desc"),
          summary: [t("bonus2.preset.adv_single.s0"), t("bonus2.preset.adv_single.s1")],
          values: { max_activations_per_player: 1, cooldown_days: null }
        },
        {
          id: "adv-weekly",
          title: t("bonus2.preset.adv_weekly.title"),
          description: t("bonus2.preset.adv_weekly.desc"),
          summary: [t("bonus2.preset.adv_weekly.s0"), t("bonus2.preset.adv_weekly.s1")],
          values: { max_activations_per_player: null, cooldown_days: 7 }
        },
        {
          id: "adv-monthly",
          title: t("bonus2.preset.adv_monthly.title"),
          description: t("bonus2.preset.adv_monthly.desc"),
          summary: [t("bonus2.preset.adv_monthly.s0"), t("bonus2.preset.adv_monthly.s1")],
          values: { max_activations_per_player: null, cooldown_days: 30 }
        },
        {
          id: "adv-limited-3",
          title: t("bonus2.preset.adv_limited_3.title"),
          description: t("bonus2.preset.adv_limited_3.desc"),
          summary: [t("bonus2.preset.adv_limited_3.s0"), t("bonus2.preset.adv_limited_3.s1")],
          values: { max_activations_per_player: 3, cooldown_days: null }
        }
      ]

    default:
      return []
  }
}

// ============================================
// SELECT OPTIONS
// ============================================

// Iteration-3 Fix 13: Cashback / Reload / Referral / VIP are hidden from the create/edit
// dropdown but KEPT in the list so existing bonus rows of those types still resolve a
// label in read/edit views. Filter on `!hidden` when rendering the selectable options.
export const BONUS_TYPE_OPTIONS = [
  { value: "DEPOSIT", label: "bonus2.opt.type.DEPOSIT" },
  { value: "NO_DEPOSIT", label: "bonus2.opt.type.NO_DEPOSIT" },
  { value: "CASHBACK", label: "bonus2.opt.type.CASHBACK", hidden: true },
  { value: "RELOAD", label: "bonus2.opt.type.RELOAD", hidden: true },
  { value: "FREE_SPINS", label: "bonus2.opt.type.FREE_SPINS" },
  { value: "DEPOSIT_AND_SPINS", label: "bonus2.opt.type.DEPOSIT_AND_SPINS" },
  { value: "REFERRAL", label: "bonus2.opt.type.REFERRAL", hidden: true },
  { value: "VIP", label: "bonus2.opt.type.VIP", hidden: true }
] as const

// Selectable (non-hidden) bonus types for the admin create/edit dropdown (Fix 13).
export const SELECTABLE_BONUS_TYPE_OPTIONS = BONUS_TYPE_OPTIONS.filter((o) => !("hidden" in o && o.hidden))

export const BONUS_MODE_OPTIONS = [
  { value: "STICKY", label: "bonus2.opt.mode.STICKY" },
  { value: "NON_STICKY", label: "bonus2.opt.mode.NON_STICKY" }
]

// Iteration-3 Fix 05 — locales the player-facing FE (frontend2) ships translations for.
// `en` is the primary (edited via the main Name/Description fields); the rest are the
// "Add language" options. Codes are 2-letter to match the stored name/description JSON keys.
export const BONUS_LOCALE_OPTIONS = [
  { code: "en", label: "bonus2.opt.locale.en" },
  { code: "de", label: "bonus2.opt.locale.de" },
  { code: "es", label: "bonus2.opt.locale.es" },
  { code: "fr", label: "bonus2.opt.locale.fr" },
  { code: "pt", label: "bonus2.opt.locale.pt" },
  { code: "pl", label: "bonus2.opt.locale.pl" },
  { code: "ru", label: "bonus2.opt.locale.ru" },
  { code: "se", label: "bonus2.opt.locale.se" },
  { code: "dk", label: "bonus2.opt.locale.dk" },
  { code: "hr", label: "bonus2.opt.locale.hr" }
]
/** Translation KEY for a locale code; falls back to the bare code when unknown. */
export const bonusLocaleLabel = (code: string): string => BONUS_LOCALE_OPTIONS.find((l) => l.code === code)?.label ?? code.toUpperCase()

// Iteration-3 Fix 14 — eligible payment classes for deposit activation. Values match the
// bonus-service provider→class mapping ("payadmit"→FIAT, "cryptochill"→CRYPTO).
export const ELIGIBLE_PAYMENT_OPTIONS = [
  { value: "FIAT", label: "bonus2.opt.payment.FIAT" },
  { value: "CRYPTO", label: "bonus2.opt.payment.CRYPTO" }
]

// Iteration-3 Fix 12 — day-of-week options for the recurring-availability window.
// value matches JS getUTCDay(): 0 = Sunday .. 6 = Saturday (bonus-service uses the same).
export const WEEKDAY_OPTIONS = [
  { value: 0, label: "bonus2.opt.weekday.0" },
  { value: 1, label: "bonus2.opt.weekday.1" },
  { value: 2, label: "bonus2.opt.weekday.2" },
  { value: 3, label: "bonus2.opt.weekday.3" },
  { value: 4, label: "bonus2.opt.weekday.4" },
  { value: 5, label: "bonus2.opt.weekday.5" },
  { value: 6, label: "bonus2.opt.weekday.6" }
]

// AI Dynamic is greyed out (visible, unselectable) — feature deferred to a later stage.
export const CALCULATION_MODE_OPTIONS = [
  { value: "PERCENTAGE", label: "bonus2.opt.calc.PERCENTAGE" },
  { value: "FIXED", label: "bonus2.opt.calc.FIXED" },
  { value: "AI_DYNAMIC", label: "bonus2.opt.calc.AI_DYNAMIC", disabled: true }
]

// Base options are AMOUNTS (used to size a requirement/cap), NOT bet types. Labelled as
// "× Bonus amount" etc. so they don't get confused with the "Which Bets Count" bet-type options.
export const WAGERING_BASE_OPTIONS = [
  { value: "BONUS_ONLY", label: "bonus2.opt.base.BONUS_ONLY" },
  { value: "DEPOSIT_ONLY", label: "bonus2.opt.base.DEPOSIT_ONLY" },
  { value: "DEPOSIT_PLUS_BONUS", label: "bonus2.opt.base.DEPOSIT_PLUS_BONUS" }
]

// Contribution options are BET TYPES (which bets progress wagering).
export const WAGERING_CONTRIBUTION_OPTIONS = [
  { value: "CASH_BETS_ONLY", label: "bonus2.opt.contribution.CASH_BETS_ONLY" },
  { value: "BONUS_BETS_ONLY", label: "bonus2.opt.contribution.BONUS_BETS_ONLY" },
  { value: "ALL_BETS", label: "bonus2.opt.contribution.ALL_BETS" }
]

export const WAGERING_ACCOUNTS_OPTIONS = [
  { value: "BONUS_ONLY", label: "bonus2.opt.accounts.BONUS_ONLY" },
  { value: "REAL_ONLY", label: "bonus2.opt.accounts.REAL_ONLY" },
  { value: "BOTH_BALANCES", label: "bonus2.opt.accounts.BOTH_BALANCES" }
]

// Iteration-3 Fix 10 — activation-event options.
//  - FIRST_DEPOSIT / RELOAD_DEPOSIT are HIDDEN from the dropdown (enum kept for existing rows).
//  - BET_THRESHOLD is relabelled "On Wager Threshold" (+ a wager-amount field in the form).
//  - VIP_LEVEL_UP (+ tier multi-select) and PLAYER_BIRTHDAY are new.
export const ACTIVATION_EVENT_OPTIONS = [
  { value: "NO_TRIGGER", label: "bonus2.opt.activation.NO_TRIGGER" },
  // Free Spins / No-Deposit only (filtered in BonusFormSections.tsx). Activated only by
  // explicit player action (game-launch claim, or the self-claim endpoint for a bonus with
  // no free spins) — never auto-promoted or swept by the grant-recovery cron.
  { value: "NO_TRIGGER_USER", label: "bonus2.opt.activation.NO_TRIGGER_USER" },
  { value: "DEPOSIT", label: "bonus2.opt.activation.DEPOSIT" },
  { value: "FIRST_DEPOSIT", label: "bonus2.opt.activation.FIRST_DEPOSIT", hidden: true },
  { value: "RELOAD_DEPOSIT", label: "bonus2.opt.activation.RELOAD_DEPOSIT", hidden: true },
  { value: "PROMO_CODE", label: "bonus2.opt.activation.PROMO_CODE" },
  { value: "BET_THRESHOLD", label: "bonus2.opt.activation.BET_THRESHOLD" },
  { value: "VIP_LEVEL_UP", label: "bonus2.opt.activation.VIP_LEVEL_UP" },
  { value: "PLAYER_BIRTHDAY", label: "bonus2.opt.activation.PLAYER_BIRTHDAY" }
] as const

// Selectable (non-hidden) activation events for the admin dropdown (Fix 10).
export const SELECTABLE_ACTIVATION_EVENT_OPTIONS = ACTIVATION_EVENT_OPTIONS.filter((o) => !("hidden" in o && o.hidden))

export const STACKING_MODE_OPTIONS = [
  { value: "NO_STACKING", label: "bonus2.opt.stacking.NO_STACKING" },
  { value: "STACK_OLDEST_ACTIVE", label: "bonus2.opt.stacking.STACK_OLDEST_ACTIVE" },
  { value: "STACK_ALL_ACTIVE", label: "bonus2.opt.stacking.STACK_ALL_ACTIVE" },
  { value: "STACK_PROPORTIONAL", label: "bonus2.opt.stacking.STACK_PROPORTIONAL" }
]

// Product eligibility (Req 11) — multi-select. Empty list = all products.
// ALL is intentionally omitted: an empty selection already means "all products".
// Slots/Live category granularity is not available yet (product-level only).
export const ALLOWED_PRODUCT_OPTIONS = [
  { value: "CASINO", label: "bonus2.opt.product.CASINO" },
  { value: "SPORTS", label: "bonus2.opt.product.SPORTS" },
  { value: "ORIGINALS", label: "bonus2.opt.product.ORIGINALS" }
]

// ============================================
// EXPIRATION HELPERS
// ============================================

/**
 * Check if a free spin is expiring within 24 hours
 */
export const isExpiringSoon = (expiresAt: string | undefined): boolean => {
  if (!expiresAt) return false
  const expiry = new Date(expiresAt)
  const now = new Date()
  const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60)
  return hoursUntilExpiry <= 24 && hoursUntilExpiry > 0
}

/**
 * Check if a free spin has expired
 */
export const isExpired = (expiresAt: string | undefined): boolean => {
  if (!expiresAt) return false
  return new Date(expiresAt) < new Date()
}

// ============================================
// DISPLAY HELPERS
// ============================================

/**
 * Extract display text from JSON localized field or plain string
 * Used for name/description fields that can be either string or { en: string, ... }
 */
export const getDisplayText = (value: string | Record<string, string> | null | undefined): string => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object" && "en" in value) return value.en
  return ""
}

/**
 * Get display name with fallback
 * Same as getDisplayText but returns "Unknown Bonus" if empty
 */
/**
 * Build the form seed for a DUPLICATE of an existing template.
 *
 * The clone is a NEW template, so everything identifying the original is dropped and the
 * configuration is carried over verbatim. Three fields are deliberately NOT copied:
 *
 * - `promo_codes` — a promo code is UNIQUE across the tenant, so copying the set would produce
 *   a form that looks fine
 *   and then fails on save with a constraint error the operator cannot interpret. Cleared, so
 *   they assign a new code (or none).
 * - `active` — a clone opens as an INACTIVE draft. Duplicating is the first half of "make a
 *   variant of this"; going live the moment the copy is saved would put a second, identical
 *   bonus in front of players before anyone changed the thing they were copying it to change.
 * - `id` / timestamps — the copy is a new row; the backend mints its own.
 *
 * `copySuffix` marks the clone apart in the list; it is passed in already translated because
 * helpers have no `t`. `name_i18n` is kept as-is: the submit path overwrites its `en` from the
 * flat name, and the remaining locales are a better starting point than nothing.
 */
export const buildDuplicateSeed = (bonus: AdminBonus2, copySuffix = "(Copy)", unknownName = "Unknown Bonus"): AdminBonus2 => {
  const { promo_code: _promoCode, promo_codes: _promoCodes, ...rest } = bonus
  return {
    ...rest,
    id: "",
    name: `${getDisplayName(bonus.name, unknownName)} ${copySuffix}`,
    active: false,
    created_at: "",
    updated_at: ""
  }
}

export const getDisplayName = (name: string | Record<string, string> | null | undefined, fallback = "Unknown Bonus"): string => {
  const text = getDisplayText(name)
  return text || fallback
}

/**
 * Get display description with fallback
 * Same as getDisplayText but returns empty string if empty (for optional fields)
 */
export const getDisplayDescription = (description: string | Record<string, string> | null | undefined): string => {
  return getDisplayText(description)
}

// ============================================
// COLOR HELPERS
// ============================================

/**
 * Get badge color class for bonus type
 */
export const getBonusTypeColor = (type: AdminBonus2["type"]): string => {
  const colors: Record<AdminBonus2["type"], string> = {
    DEPOSIT: "bg-green",
    NO_DEPOSIT: "bg-blue-500",
    CASHBACK: "bg-purple",
    RELOAD: "bg-orange-500",
    FREE_SPINS: "bg-pink-500",
    DEPOSIT_AND_SPINS: "bg-indigo-500",
    REFERRAL: "bg-teal-500",
    VIP: "bg-yellow-500"
  }
  return colors[type] || "bg-gray-500"
}

/**
 * Get badge color class for bonus mode
 */
export const getBonusModeColor = (mode: AdminBonus2["mode"]): string => {
  const colors: Record<AdminBonus2["mode"], string> = {
    STICKY: "bg-red",
    NON_STICKY: "bg-green"
  }
  return colors[mode] || "bg-gray-600"
}

/**
 * Get badge color class for player bonus status
 */
export const getPlayerBonusStatusColor = (status: AdminPlayerBonus2["status"]): string => {
  const colors: Record<AdminPlayerBonus2["status"], string> = {
    SUBSCRIBED: "bg-blue-500",
    ACTIVE: "bg-green",
    COMPLETED: "bg-purple",
    EXPIRED: "bg-orange-500",
    CANCELLED: "bg-red"
  }
  return colors[status] || "bg-gray-500"
}

/**
 * Get badge color class for violation type
 */
export const getViolationTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    MAX_BET_EXCEEDED: "bg-rose-600",
    RESTRICTED_GAME: "bg-orange-600",
    FORBIDDEN_ACTION: "bg-rose-700",
    WAGERING_VIOLATION: "bg-yellow-600"
  }
  return colors[type] || "bg-gray-600"
}
